import { auth } from '@/shared/lib/auth';
import { CUSTOM_FRESHCHAT_ID_CLASSNAME, FRESHCHAT_TIMEOUT } from './constant';
import handleUpdateFreshChatRestoreId from './restoreId';
import './freshchat.scss';

let _freshChatWidgetActive = false;
/**
 * Reports whether the Freshchat support widget is currently mounted. Returns the module-level active flag maintained by the add and remove helpers.
 *
 * @returns True while the Freshchat widget is active.
 */
const freshChatWidgetActive = () => _freshChatWidgetActive;
/**
 * Initialises the loaded Freshchat widget for a user and wires its events to the custom launcher.
 * Calls fcWidget.init with the user details and stored restore id, sets the Facility property on
 * the existing chat user or, for a new one, once user:created fires and persists its restore id,
 * then reveals and animates the launcher on load, opens the Support requests topic on first open,
 * re-shows the launcher on close and mirrors the unread count into the badge.
 *
 * @param props - The user's firstName, lastName, email, phone, userId and facility.
 * @returns Nothing.
 */
const initFreshChat = ({ firstName, lastName, email, phone, userId, facility }: any) => {
  (window as any).fcWidget.init({
    token: '0bc651bb-d071-48ee-a8f6-69be99da8491',
    host: 'https://btcy.freshchat.com',
    config: {
      headerProperty: {
        hideChatButton: true,
      },
    },
    externalId: userId,
    restoreId: auth.freshChatRestoreId() || null,
    firstName,
    lastName,
    email,
    phone,
    Facility: facility,
  });

  (window as any).fcWidget.user.get((resp: any) => {
    const status = resp?.status;
    if (status === 200) {
      // *: Check chat history expiration (30 days)
      // const lastSeenMillis = resp?.data?.lastSeenMillis;
      // if (lastSeenMillis && dayjs().diff(dayjs(lastSeenMillis), 'day') > 30) {
      //   (window as any).fcWidget.user.clear();
      //   handleUpdateFreshChatRestoreId('');
      // }

      (window as any).fcWidget.user.setProperties({
        Facility: facility,
      });
    } else {
      // *: Trigger this event after creating user at the first time
      (window as any).fcWidget.on('user:created', (resp: any) => {
        const status = resp?.status;
        const data = resp?.data;
        if (status === 200) {
          (window as any).fcWidget.user.setProperties({
            Facility: facility,
          });

          if (data.restoreId) {
            handleUpdateFreshChatRestoreId(data.restoreId);
          }
        }
      });
    }
  });

  (window as any).fcWidget.on('widget:loaded', () => {
    // *: Show Freshchat button
    const container = document.getElementById(CUSTOM_FRESHCHAT_ID_CLASSNAME.CONTAINER_ID);
    if (container) {
      container.style.visibility = 'visible';
    }

    const freshChatButton = document.getElementById(CUSTOM_FRESHCHAT_ID_CLASSNAME.BUTTON_ID);
    if (freshChatButton) {
      freshChatButton.style.animation = `freshChatButtonAnimation 0.3s alternate ${FRESHCHAT_TIMEOUT}s backwards`;
    }

    const hotlineIcon = document.getElementById(CUSTOM_FRESHCHAT_ID_CLASSNAME.HOTLINE_ICON_ID);
    if (hotlineIcon) {
      hotlineIcon.style.animation = `hotlineImageAnimation 0.3s alternate ${FRESHCHAT_TIMEOUT}s backwards`;
    }

    // *: Add animaion to pseudo ::after
    freshChatButton?.classList.add('--default');
    setTimeout(() => {
      freshChatButton?.classList.remove('--default');
    }, FRESHCHAT_TIMEOUT * 1000);
  });

  let prefilled = false;
  (window as any).fcWidget.on('widget:opened', () => {
    // *: Hide Freshchat button
    const openedButton = document.getElementById(CUSTOM_FRESHCHAT_ID_CLASSNAME.BUTTON_ID);
    if (openedButton) {
      openedButton.style.visibility = 'hidden';
    }

    // *: Open "Support requests" topic by default
    if (!prefilled) {
      (window as any).fcWidget.open({ name: 'Support requests' });
      prefilled = true;
    }
  });

  (window as any).fcWidget.on('widget:closed', () => {
    const frame = document.getElementById(CUSTOM_FRESHCHAT_ID_CLASSNAME.FRAME_ID);
    if (frame) {
      frame.style.visibility = 'hidden';
    }

    // *: Set timeout to prevent from keeping privous hover state
    setTimeout(() => {
      const closedButton = document.getElementById(CUSTOM_FRESHCHAT_ID_CLASSNAME.BUTTON_ID);
      if (closedButton) {
        closedButton.style.visibility = 'visible';
      }
    }, 100);
  });

  (window as any).fcWidget.on('unreadCount:notify', (resp: any) => {
    const notify = document.getElementById(CUSTOM_FRESHCHAT_ID_CLASSNAME.NOTIFY_ID);
    if (!notify) {
      return;
    }
    notify.textContent = resp.count > 9 ? '9+' : resp.count;
    if (resp.count > 0) {
      notify.style.visibility = 'visible';
    } else if (resp.count === 0) {
      notify.style.visibility = 'hidden';
    }
  });
};

/**
 * Ensures the Freshchat widget SDK is present in the document before initialising it for a user. Initialises straight away when the script element already exists, otherwise creates an async script pointing at the hosted widget bundle, stamps it with the CSP nonce, appends it to head and initialises Freshchat from its onload.
 *
 * @param i - Document used to look up and create the script element.
 * @param t - Element id of the Freshchat SDK script.
 * @param userInfo - User details passed to the Freshchat initialisation.
 * @returns Nothing.
 */
const initialize = (i: any, t: any, userInfo: any) => {
  if (i.getElementById(t)) {
    initFreshChat(userInfo);
    return;
  }

  const e = i.createElement('script');
  e.id = t;
  e.async = true;
  e.src = 'https://public.cdn.biotricity.com/web_libs/freshchat/widget.js';
  e.setAttribute('nonce', (window as any).__CSP_NONCE__);
  e.onload = () => initFreshChat(userInfo);
  i.head.appendChild(e);
};

/**
 * Starts Freshchat widget loading for a user against the live document. Delegates to the loader with the document and the 'Freshchat-js-sdk' script id.
 *
 * @param userInfo - User details passed to the Freshchat initialisation.
 * @returns Nothing.
 */
const initiateCall = (userInfo: any) => {
  initialize(document, 'Freshchat-js-sdk', userInfo);
};
/**
 * Mounts the Freshchat support widget for a signed-in user. Raises the module-level active flag and triggers the SDK load and initialisation.
 *
 * @param userInfo - User details passed to the Freshchat initialisation.
 * @returns Nothing.
 */
const addFreshChatWidget = (userInfo: any) => {
  _freshChatWidgetActive = true;
  initiateCall(userInfo);
};

/**
 * Tears the Freshchat support widget down, for instance on logout. Clears the module-level active flag, clears the widget's user data and destroys the widget once that clear settles.
 *
 * @returns Nothing.
 */
const removeFreshChatWidget = () => {
  _freshChatWidgetActive = false;
  (window as any).fcWidget.user.clear().finally(() => {
    (window as any).fcWidget.destroy();
  });
};

export { addFreshChatWidget, removeFreshChatWidget, freshChatWidgetActive };
